// Activity Center store — the header "something is working" indicator.
//
// Polls GET /api/jobs (the household's running background jobs: caption
// translations today, audio dubs later) and exposes them to the header
// ActivityCenter dropdown. Mirrors the swallow-errors-never-throw style of
// activePlayback.js / HandoffBanner: a transient failure keeps the last known
// list rather than flapping the UI.
import { defineStore } from "pinia";
import { useAuthStore } from "@/stores/auth";

export const useJobsStore = defineStore("jobs", {
  state: () => ({
    jobs: [], // [{ id, kind, label, progress, done, total, targetLang, fileId }]
    loaded: false,
    _timer: null,
    _subscribers: 0, // ref-count so multiple mounted indicators share one poller
  }),
  getters: {
    activeCount: (s) => s.jobs.length,
    hasActive: (s) => s.jobs.length > 0,
    // The jobs for a given source file (so the player can detect an in-flight
    // translation to re-attach to after a refresh).
    jobsForFile: (s) => (fileId) =>
      s.jobs.filter(
        (j) => fileId != null && String(j.fileId) === String(fileId),
      ),
  },
  actions: {
    async refresh() {
      // Hidden tab → skip the poll; the interval self-heals on the next
      // visible tick (no point burning requests nobody can see).
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      )
        return;
      const auth = useAuthStore();
      if (!auth.isUnlocked) {
        this.jobs = [];
        return;
      }
      try {
        const r = await fetch("/api/jobs", {
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        if (!r.ok) return; // transient (401 during reload, 5xx) → keep last known
        const j = await r.json();
        this.jobs = Array.isArray(j?.jobs) ? j.jobs : [];
        this.loaded = true;
      } catch {
        /* network blip — keep the last known list */
      }
    },
    async cancel(id) {
      // Optimistic: mark it canceling immediately, but keep it visible until the
      // server-side runner actually unwinds.
      const job = this.jobs.find((j) => j.id === id);
      if (job) job.status = "canceling";
      try {
        const r = await fetch(
          "/api/jobs/" + encodeURIComponent(id) + "/cancel",
          {
            method: "POST",
            credentials: "include",
            headers: { Accept: "application/json" },
          },
        );
        if (!r.ok) throw new Error("cancel failed");
      } catch {
        /* ignore — the next poll reconciles */
      }
      this.refresh();
    },
    startPolling(intervalMs = 6000) {
      this._subscribers += 1;
      if (this._timer) return; // a poller is already running for another mount
      this.refresh();
      this._timer = setInterval(() => this.refresh(), intervalMs);
    },
    stopPolling() {
      this._subscribers = Math.max(0, this._subscribers - 1);
      if (this._subscribers === 0 && this._timer) {
        clearInterval(this._timer);
        this._timer = null;
      }
    },
  },
});
